export type RecorderState = 'unsupported' | 'idle' | 'recording' | 'pending';

export type RecorderErrorKind = 'permission' | 'unknown';

/** A finished recording, kept to send again when the first try did not go through. */
export interface Recording {
  blob: Blob;
  mimeType: string;
  durationMs: number;
}

/**
 * When the screen must offer the chat instead of the microphone: the device
 * cannot record at all, the person denied the microphone, or the recorder
 * failed otherwise (no microphone, one busy elsewhere) — never a dead button.
 * The last two keep a retry.
 */
export function recorderFallback(
  state: RecorderState,
  lastError: RecorderErrorKind | null,
): 'none' | 'unsupported' | 'denied' | 'failed' {
  if (state === 'unsupported') return 'unsupported';
  if (state === 'idle' && lastError === 'permission') return 'denied';
  if (state === 'idle' && lastError === 'unknown') return 'failed';
  return 'none';
}

/**
 * Whether a stopped recording goes to the screen: not when it was cancelled,
 * shorter than a deliberate recording, or its screen is gone (leaving a screen
 * stops the microphone, and some browsers still hand the recording over).
 */
export function shouldDeliver(r: { cancelled: boolean; mounted: boolean; durationMs: number; minDurationMs: number }): boolean {
  return !r.cancelled && r.mounted && r.durationMs >= r.minDurationMs;
}

/**
 * Hands the recording to `send` (true: accepted). Returns the recording to keep
 * for «Отправить ещё раз» when it was refused or the upload failed, else null.
 */
export async function sendRecording(send: (rec: Recording) => Promise<boolean>, rec: Recording): Promise<Recording | null> {
  try {
    return (await send(rec)) ? null : rec;
  } catch {
    return rec;
  }
}
